/**
 * Button Component
 * ================
 * Reusable button component with multiple variants and sizes.
 */

import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Button variants configuration
 */
const variants = {
  primary: 'bg-dme-orange text-white hover:bg-dme-orange-dark hover:shadow-orange',
  secondary: 'bg-white text-dme-blue border-2 border-dme-blue hover:bg-dme-blue hover:text-white',
  outline: 'bg-transparent text-dme-blue border-2 border-dme-blue hover:bg-dme-blue hover:text-white',
  'outline-white': 'bg-transparent text-white border-2 border-white hover:bg-white hover:text-dme-blue',
  ghost: 'bg-transparent text-dme-gray-600 hover:text-dme-blue hover:bg-dme-blue-50',
  'ghost-white': 'bg-transparent text-white/80 hover:text-white hover:bg-white/10',
  danger: 'bg-red-500 text-white hover:bg-red-600 hover:shadow-lg',
  success: 'bg-dme-green text-white hover:bg-dme-green-dark hover:shadow-lg',
};

/**
 * Button sizes configuration
 */
const sizes = {
  xs: 'px-3 py-1.5 text-xs',
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
  xl: 'px-10 py-5 text-xl',
};

/**
 * Button Component
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {string} [props.variant='primary'] - Button variant
 * @param {string} [props.size='md'] - Button size
 * @param {boolean} [props.isLoading=false] - Loading state
 * @param {boolean} [props.isDisabled=false] - Disabled state
 * @param {boolean} [props.fullWidth=false] - Full width button
 * @param {React.ElementType} [props.as='button'] - Element to render as
 * @param {React.ReactNode} [props.leftIcon] - Icon to show before text
 * @param {React.ReactNode} [props.rightIcon] - Icon to show after text
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onClick] - Click handler
 * @returns {JSX.Element} Button component
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  isDisabled = false,
  fullWidth = false,
  as: Component = 'button',
  leftIcon,
  rightIcon,
  className = '',
  onClick,
  ...props
}) {
  // Determine disabled state
  const disabled = isDisabled || isLoading;
  
  // Base classes
  const baseClasses = `
    inline-flex items-center justify-center gap-2
    font-semibold rounded-lg
    transition-all duration-300
    focus:outline-none focus:ring-2 focus:ring-dme-orange focus:ring-offset-2
    disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:transform-none
    active:scale-[0.98]
  `;
  
  // Combine all classes
  const classes = `
    ${baseClasses}
    ${variants[variant] || variants.primary}
    ${sizes[size] || sizes.md}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `;
  
  return (
    <Component
      className={classes}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {/* Loading Spinner */}
      {isLoading && (
        <Loader2 className="w-5 h-5 animate-spin" />
      )}
      
      {/* Left Icon */}
      {!isLoading && leftIcon && (
        <span className="flex-shrink-0">{leftIcon}</span>
      )}
      
      {/* Button Text */}
      <span>{children}</span>
      
      {/* Right Icon */}
      {!isLoading && rightIcon && (
        <span className="flex-shrink-0">{rightIcon}</span>
      )}
    </Component>
  );
}

/**
 * IconButton Component
 * Button that only contains an icon.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.icon - Icon to display
 * @param {string} [props.ariaLabel] - Accessible label
 * @param {string} [props.size='md'] - Button size
 * @param {string} [props.variant='ghost'] - Button variant
 * @returns {JSX.Element} IconButton component
 */
export function IconButton({
  icon,
  ariaLabel,
  size = 'md',
  variant = 'ghost',
  className = '',
  ...props
}) {
  const sizeClasses = {
    xs: 'p-1.5',
    sm: 'p-2',
    md: 'p-2.5',
    lg: 'p-3',
    xl: 'p-4',
  };
  
  const iconSizes = {
    xs: 'w-4 h-4',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-7 h-7',
  };
  
  const classes = `
    inline-flex items-center justify-center
    rounded-lg transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-dme-orange focus:ring-offset-2
    disabled:opacity-60 disabled:cursor-not-allowed
    hover:scale-105 active:scale-95
    ${variants[variant] || variants.ghost}
    ${sizeClasses[size] || sizeClasses.md}
    ${className}
  `;
  
  return (
    <button
      className={classes}
      aria-label={ariaLabel}
      {...props}
    >
      {React.cloneElement(icon, { 
        className: `${iconSizes[size] || iconSizes.md} ${icon.props.className || ''}` 
      })}
    </button>
  );
}

export default Button;
